// ============================================================
// Scheduled protocols - the activation engine (2026-07-16).
//
// A protocol saved with a future start date sits dormant
// (status='scheduled', is_active=false, vials parked) until its day
// arrives. There is no server cron: this module reconciles CLIENT-SIDE
// whenever the app is alive, promoting due scheduled protocols to
// active. Plan: docs/2026-07-16-scheduled-activation-phase4-plan.md.
//
//   trigger (boot / foreground / protocols-change / web day tick)
//        │
//        ▼
//   reconcileScheduledProtocols()          [single-flight]
//        │  fetchDueScheduledProtocols(local today)   - none? exit
//        │  fetchInventory() once
//        ▼
//   per protocol: partitionProtocolVials()
//        ├─ any unmixed requiresMixOnStart vial? ──> stay scheduled
//        │     (HomePage renders the "Time to start - mix" prompt;
//        │      Start-Using flips the vial; the next trigger lands here
//        │      again and completes activation)
//        └─ ready ──> activate_scheduled_protocol RPC (atomic:
//              status+is_active+start_date restamp + vial stamps)
//              └─> ensureRemindersForProtocol (idempotent, honors
//                  notifyEachDose) - ALSO on the lost-race error (OV7)
//
// Timezone rule: every date comparison uses LOCAL calendar days
// (parseLocalDate / localTodayStr), never UTC parses.
// ============================================================

import type { InventoryItem, Protocol } from '../data/types';
import {
  activateScheduledProtocol,
  fetchDueScheduledProtocols,
  fetchInventory,
  refreshProtocolSummary,
  subscribe,
  updateScheduledStartDate,
  type ActivationReconstitution,
} from '../data/cloud';
import { supabase } from '../lib/supabase';
import { isNative } from './native';
import { onAppForeground } from './native';
import { calculateDosesPerVial, deriveMixtureFields } from './calculations';
import { ensureRemindersForProtocol } from './reminders';
import { trackEvent } from './analytics';

/** Local calendar date as yyyy-mm-dd (matches buildProtocol's localDateStr).
 *  Optional day offset for picker bounds (e.g. localTodayStr(90) = max). */
export function localTodayStr(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export interface VialPartition {
  /** Live parked vials that must be MIXED by the user before this protocol
   *  can activate (powder / sealed-combo components, still researching). */
  blockingVials: InventoryItem[];
  /** Client-derived stamps for the RPC: still-parked vials get full counts
   *  (null for approximate - the C6 clamp survives), just-mixed vials get
   *  their missing counts filled server-side (1A). */
  reconstitutions: ActivationReconstitution[];
  /** Vials that were already fully started (counts present) - skipped, and
   *  logged for visibility (OV6). */
  skippedVialIds: string[];
}

/**
 * Pure partition of a due scheduled protocol's linked vials. Missing and
 * archived vials are excluded from EVERYTHING (2A/OV8): they can neither
 * block activation nor appear in the RPC payload - the protocol activates
 * and that component degrades to untracked, the same posture as a failed
 * vial write at save time.
 */
export function partitionProtocolVials(
  protocol: Protocol,
  inventory: InventoryItem[],
): VialPartition {
  const byId = new Map(inventory.map((v) => [v.id, v]));
  const blockingVials: InventoryItem[] = [];
  const reconstitutions: ActivationReconstitution[] = [];
  const skippedVialIds: string[] = [];
  const seen = new Set<string>();

  for (const component of protocol.components) {
    if (!component.inventoryId || seen.has(component.inventoryId)) continue;
    seen.add(component.inventoryId);
    const vial = byId.get(component.inventoryId);
    // 2A/OV8: deleted or archived vials are out of the picture entirely.
    if (!vial || !vial.isActive) continue;

    const needsMix = !!component.requiresMixOnStart && !!vial.isResearching;
    if (needsMix) {
      blockingVials.push(vial);
      continue;
    }

    if (!vial.isResearching && vial.totalDoses != null) {
      // Fully started already (early start slipped past the guards, or a
      // prior partial activation) - idempotent skip, visible in telemetry.
      skippedVialIds.push(vial.id);
      continue;
    }

    if (component.approximate) {
      // C6: never fabricate counts for a vial whose size/water the user
      // said they don't know. Flip only (null counts are the established
      // "log a dose to start counting" state).
      reconstitutions.push({
        inventoryId: vial.id,
        totalDoses: null,
        dosesRemaining: null,
      });
      continue;
    }

    if (vial.mixGroupId && vial.mixRecipe && vial.mixRecipe.length >= 2 && component.drawUnits) {
      // Combined vial: re-run the exact save-time derivation from the
      // vial's REAL stored recipe + water (C7 sole-derivation-writer).
      const derived = deriveMixtureFields(
        vial.mixRecipe,
        vial.bacWaterMl,
        component.drawUnits,
        component.syringeType,
      );
      reconstitutions.push({
        inventoryId: vial.id,
        bacWaterMl: vial.bacWaterMl,
        concentrationMcgPerUnit: derived.concentrationMcgPerUnit,
        totalDoses: derived.totalDoses,
        dosesRemaining: derived.totalDoses,
      });
      continue;
    }

    // Single vial: counts from the component's PHASE 1 dose - activation is
    // by definition the start of phase 1, and "current phase" math against
    // a not-yet-restamped start_date would drift for late activations.
    const doseMcg = component.phases[0]?.doseMcg ?? 0;
    const totalDoses = doseMcg > 0 ? calculateDosesPerVial(vial.vialSizeMg, doseMcg) || null : null;
    reconstitutions.push({
      inventoryId: vial.id,
      totalDoses,
      dosesRemaining: totalDoses,
    });
  }

  return { blockingVials, reconstitutions, skippedVialIds };
}

/**
 * The scheduled protocol (if any) whose components reserve this vial.
 * Early-start guards use it: a vial saved for a scheduled protocol must
 * not be started or dosed from another screen before its day (that
 * reintroduces the Bug-2 clock/alert mess and double-books the supply).
 * Callers decide what "before its day" means - Start-Using allows the
 * vial ON/AFTER the start date (mixing IS the activation path), ad-hoc
 * logging blocks it whenever the protocol is still scheduled.
 */
export function scheduledProtocolHoldingVial(
  vialId: string,
  protocols: Protocol[],
): Protocol | null {
  for (const p of protocols) {
    if (p.status !== 'scheduled') continue;
    if (p.components.some((c) => c.inventoryId === vialId)) return p;
  }
  return null;
}

/** Due scheduled protocols that are only waiting on the user to mix -
 *  HomePage renders these as "Time to start" prompts. Pure. */
export function pendingMixProtocols(
  protocols: Protocol[],
  inventory: InventoryItem[],
  today = localTodayStr(),
): Array<{ protocol: Protocol; vials: InventoryItem[] }> {
  return protocols
    .filter((p) => p.status === 'scheduled' && p.startDate <= today)
    .map((p) => ({ protocol: p, vials: partitionProtocolVials(p, inventory).blockingVials }))
    .filter((x) => x.vials.length > 0);
}

// ────────────────────────────────────────────────────────────
// "Start now" (detail page) - activate a scheduled protocol early
// ────────────────────────────────────────────────────────────

export type StartNowOutcome =
  | { kind: 'activated' }
  /** A powder/sealed vial still needs the user's hands - route to Start-Using. */
  | { kind: 'needs_mix'; vial: InventoryItem }
  /** Another device (or the reconcile engine) got there first. */
  | { kind: 'already' };

/**
 * User-initiated early start. Same atomic flip + restamp as the reconcile
 * engine (D-Restamp makes "Start now" genuinely start TODAY); powder still
 * routes through the mix. Throws only on real failures (network, RPC
 * validation) - callers toast and leave the protocol scheduled.
 */
export async function startScheduledProtocolNow(
  protocol: Protocol,
  inventory: InventoryItem[],
): Promise<StartNowOutcome> {
  const { blockingVials, reconstitutions } = partitionProtocolVials(protocol, inventory);
  if (blockingVials.length > 0) {
    // Early start with an unmixed vial: the mix IS the start, so restamp
    // the start date to TODAY before handing off to Start-Using. Without
    // this the Start-Using early-start guard rejects the vial ("saved for
    // X, which starts July 17") and the reconcile engine would not pick
    // the protocol up after the mix - the Start now button dead-ended.
    if (protocol.startDate && protocol.startDate > localTodayStr()) {
      const restamped = await updateScheduledStartDate(protocol.id, localTodayStr());
      if (!restamped) return { kind: 'already' };
    }
    return { kind: 'needs_mix', vial: blockingVials[0] };
  }
  let raced = false;
  try {
    await activateScheduledProtocol(protocol.id, localTodayStr(), reconstitutions);
  } catch (err) {
    if ((err as Error)?.name !== 'ProtocolNotScheduledError') throw err;
    raced = true; // still repair reminders below (OV7)
  }
  if (protocol.notifyEachDose) {
    try {
      await ensureRemindersForProtocol({ ...protocol, status: 'active', isActive: true });
    } catch (err) {
      console.warn('[activation] reminder ensure failed (protocol active):', err);
    }
  }
  if (!raced) {
    // The stored summary still says "You start on July 17." - regenerate
    // now that the protocol is running (fire-and-forget, respects a
    // user-edited summary).
    refreshProtocolSummary(protocol.id, 'You started this protocol today.');
    void trackEvent('protocol_started_early', {
      protocolId: protocol.id,
      scheduledFor: protocol.startDate,
      startedOn: localTodayStr(),
    });
  }
  return raced ? { kind: 'already' } : { kind: 'activated' };
}

// ────────────────────────────────────────────────────────────
// The reconcile routine
// ────────────────────────────────────────────────────────────

let reconcileInFlight = false;
// A trigger that lands mid-run queues ONE follow-up pass instead of being
// dropped - e.g. the user confirms a Start-Using mix while a foreground
// reconcile is still running; swallowing that trigger would leave a
// mixed-but-scheduled protocol in limbo until the next app cycle
// (adversarial review A4).
let reconcileRerunQueued = false;

export interface ReconcileResult {
  activated: number;
  awaitingMix: number;
  errors: number;
}

export async function reconcileScheduledProtocols(): Promise<ReconcileResult> {
  const result: ReconcileResult = { activated: 0, awaitingMix: 0, errors: 0 };
  if (reconcileInFlight) {
    reconcileRerunQueued = true;
    return result;
  }
  reconcileInFlight = true;
  try {
    // Same auth gate as the reminder engine: unauthed routes must not
    // spam "Not authenticated" errors.
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return result;

    const today = localTodayStr();
    const due = await fetchDueScheduledProtocols(today);
    if (due.length === 0) return result;

    const inventory = await fetchInventory();

    for (const protocol of due) {
      try {
        const { blockingVials, reconstitutions, skippedVialIds } =
          partitionProtocolVials(protocol, inventory);

        if (blockingVials.length > 0) {
          // Powder path: the user has to physically mix. Stay scheduled;
          // the dashboard prompt + cue notification own the nudge.
          result.awaitingMix++;
          continue;
        }

        for (const id of skippedVialIds) {
          void trackEvent('scheduled_activation_vial_skipped', {
            protocolId: protocol.id,
            inventoryId: id,
          });
        }

        try {
          await activateScheduledProtocol(protocol.id, today, reconstitutions);
          result.activated++;
          // Drop the baked "You start on <date>." line now that it runs.
          refreshProtocolSummary(protocol.id, 'This protocol started today.');
          void trackEvent('protocol_auto_activated', {
            protocolId: protocol.id,
            scheduledFor: protocol.startDate,
            activatedOn: today,
            vialsStamped: reconstitutions.length,
          });
        } catch (err) {
          if ((err as Error)?.name !== 'ProtocolNotScheduledError') throw err;
          // Another device won the race (or the user paused meanwhile).
          // Fall through to the reminder repair below (OV7): a device that
          // died between its flip and its reminders is fixed by this one.
        }

        if (protocol.notifyEachDose) {
          try {
            await ensureRemindersForProtocol({ ...protocol, status: 'active', isActive: true });
          } catch (err) {
            // Reminders are repairable on any later trigger; the activation
            // itself already committed.
            console.warn('[activation] reminder ensure failed (protocol active):', err);
          }
        }
      } catch (err) {
        // One stuck protocol must not block the others; it stays scheduled
        // and retries on the next trigger.
        result.errors++;
        console.error(`[activation] could not activate protocol ${protocol.id}:`, err);
      }
    }
    return result;
  } catch (err) {
    console.error('[activation] reconcile failed:', err);
    result.errors++;
    return result;
  } finally {
    reconcileInFlight = false;
    if (reconcileRerunQueued) {
      reconcileRerunQueued = false;
      // State changed mid-run (a mix landed, a protocol was saved) - run
      // once more so the change is reconciled now, not next app-open.
      void reconcileScheduledProtocols().catch((err) => {
        console.error('[activation] queued reconcile failed:', err);
      });
    }
  }
}

// ────────────────────────────────────────────────────────────
// Engine start/stop (mirrors startReminderEngine's shape)
// ────────────────────────────────────────────────────────────

let engineStarted = false;
let unsubscribeProtocols: (() => void) | null = null;
let unsubscribeInventory: (() => void) | null = null;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let dayTickInterval: ReturnType<typeof setInterval> | null = null;
let lastReconcileDay: string | null = null;

function runReconcile(): void {
  const day = localTodayStr();
  reconcileScheduledProtocols()
    .then((result) => {
      // Stamp the day only on a CLEAN run - stamping optimistically made
      // the web day-tick skip retries for ~24h after one transient
      // failure at midnight (adversarial review A7).
      if (result.errors === 0) lastReconcileDay = day;
    })
    .catch((err) => {
      console.error('[activation] reconcile failed:', err);
    });
}

function scheduleReconcile(): void {
  // Coalesce bursts (a wizard save fires several notifies back-to-back).
  // Loop-safe: activation's own notify('protocols') re-enters here, but the
  // follow-up reconcile finds nothing due and does not write again.
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    runReconcile();
  }, 250);
}

export function startActivationEngine(): void {
  if (engineStarted) return;
  engineStarted = true;

  // Boot: catch anything that came due while the app was closed.
  runReconcile();

  // "Picked today as the start date" saves activate within the session,
  // and inventory changes catch a Start-Using mix completing a due powder
  // protocol (updateInventoryItem notifies 'inventory', not 'protocols').
  unsubscribeProtocols = subscribe('protocols', scheduleReconcile);
  unsubscribeInventory = subscribe('inventory', scheduleReconcile);

  // Native: a backgrounded app crossing midnight/start-date catches up on
  // foreground. (There is no native polling tick - iOS suspends JS.)
  onAppForeground(scheduleReconcile);

  // Web only: an app left open across local midnight has no foreground
  // event - a cheap day-rollover watcher covers it.
  if (!isNative()) {
    dayTickInterval = setInterval(() => {
      if (localTodayStr() !== lastReconcileDay) scheduleReconcile();
    }, 60_000);
  }
}

export function stopActivationEngine(): void {
  engineStarted = false;
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  if (dayTickInterval) {
    clearInterval(dayTickInterval);
    dayTickInterval = null;
  }
  if (unsubscribeProtocols) {
    unsubscribeProtocols();
    unsubscribeProtocols = null;
  }
  if (unsubscribeInventory) {
    unsubscribeInventory();
    unsubscribeInventory = null;
  }
  // Note: onAppForeground's Capacitor listener is not torn down - the
  // engine lives for the app's lifetime (Layout unmount only happens in
  // tests/HMR) and scheduleReconcile no-ops safely regardless.
}
